"use client";

import type { ReactNode } from "react";
import { motion } from "framer-motion";
import { Suspense } from "react";
import dynamic from "next/dynamic";
import { PillBadge } from "@/components/ui/PillBadge";
import { PlaceholderImage } from "@/components/ui/PlaceholderImage";
import { ViewerLoader } from "@/components/3d/ViewerLoader";
import {
  fadeUp,
  staggerContainer,
  staggerItem,
  fadeRight,
  fadeLeft,
  viewportOnce
} from "@/lib/motion";
import { AccentColor } from "@/lib/colors";

const ModelViewer = dynamic(
  () => import("@/components/3d/ModelViewer").then((mod) => mod.ModelViewer),
  {
    ssr: false,
    loading: () => <ViewerLoader className="w-full h-full" />
  }
);

type Layout = "three-images" | "text-left" | "text-right";

interface FeatureSectionProps {
  pillLabel: string;
  color: AccentColor;
  title: string;
  body: string;
  layout: Layout;
  images?: { src?: string; alt?: string; label?: string }[];
  videoSrc?: { mp4?: string; webm?: string };
  modelSrc?: {
    url: string;
    tint?: string;
    preserveMaterials?: boolean;
    rotationOffset?: [number, number, number];
  };
  /** When provided (text-left/text-right layouts), replaces the built-in image/video/model rendering. */
  media?: ReactNode;
}

export function FeatureSection({
  pillLabel,
  color,
  title,
  body,
  layout,
  images = [],
  videoSrc,
  modelSrc,
  media
}: FeatureSectionProps) {
  if (layout === "three-images") {
    return (
      <motion.section
        className="py-12 lg:py-20 px-6"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={fadeUp}
      >
        <div className="mx-auto max-w-7xl">
          <PillBadge label={pillLabel} variant={color} />
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-text-primary mt-4">
            {title}
          </h2>
          <p className="mt-4 text-text-body max-w-2xl leading-relaxed">
            {body}
          </p>
          <motion.div
            className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
          >
            {[0, 1, 2].map((i) => (
              <motion.div key={i} variants={staggerItem}>
                <PlaceholderImage
                  height="h-[280px]"
                  label={images[i]?.label || `Image ${i + 1}`}
                  src={images[i]?.src}
                  alt={images[i]?.alt}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>
    );
  }

  const isTextLeft = layout === "text-left";
  const imageVariant = isTextLeft ? fadeRight : fadeLeft;
  const textVariant = isTextLeft ? fadeUp : fadeUp;

  return (
    <section className="py-12 lg:py-20 px-6">
      <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <motion.div
          className={isTextLeft ? "" : "order-1 lg:order-2"}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={textVariant}
        >
          <PillBadge label={pillLabel} variant={color} />
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-text-primary mt-4 whitespace-pre-line">
            {title}
          </h2>
          <p className="mt-4 text-text-body leading-relaxed">{body}</p>
        </motion.div>
        <motion.div
          className={isTextLeft ? "" : "order-2 lg:order-1"}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={imageVariant}
        >
          {media ? (
            media
          ) : modelSrc ? (
            <div
              className="rounded-2xl border border-border overflow-hidden flex items-center justify-center h-[380px]"
              style={{
                background:
                  "linear-gradient(135deg, var(--color-bg-surface) 0%, var(--color-bg-elevated) 50%, var(--color-bg-surface) 100%)"
              }}
            >
              <Suspense fallback={<div className="w-full h-full" />}>
                <ModelViewer
                  modelUrl={modelSrc.url}
                  tint={modelSrc.tint}
                  className="w-full h-full"
                  followMouse
                  preserveMaterials={modelSrc.preserveMaterials}
                  rotationOffset={modelSrc.rotationOffset}
                />
              </Suspense>
            </div>
          ) : videoSrc ? (
            <video
              className="w-full rounded-2xl border border-border"
              aria-label={`${title}: feature demo`}
              autoPlay
              loop
              muted
              playsInline
            >
              {videoSrc.webm && (
                <source src={videoSrc.webm} type="video/webm" />
              )}
              {videoSrc.mp4 && <source src={videoSrc.mp4} type="video/mp4" />}
            </video>
          ) : (
            <PlaceholderImage
              height="h-[380px]"
              label={images[0]?.label || "Screenshot"}
              src={images[0]?.src}
              alt={images[0]?.alt}
            />
          )}
        </motion.div>
      </div>
    </section>
  );
}
